import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  CONFIGURACION_FICHA_RECOGER_ORDENES,
  CONFIGURACION_TABLA_RECOGER_ORDENES,
} from './utilidades/recoger-ordenes.static';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { ResponsiveService } from 'src/app/nucleo/utilidades/responsive.service';
import { TablaAsincronaComponent } from 'src/app/compartido/tabla-asincrona/tabla-asincrona.component';
import { FormulariosService } from 'src/app/nucleo/utilidades/formularios.service';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import {
  formatearFecha,
  valorNumérico,
} from 'src/app/compartido/utilidades/valores.utils';
import { obtenerFiltro } from 'src/app/compartido/tabla/utilidades/tabla.utils';
import { OrdenesService } from '../../utilidades/ordenes.service';
import { SourceTablaOrdenes } from '../../utilidades/source-tabla-ordenes.class';
import { DialogosService } from 'src/app/nucleo/utilidades/dialogos.service';
import { ConfiguracionDialogoVistaPrevia } from 'src/app/compartido/dialogos/dialogo-vista-previa/utilidades/dialogo-vista-previa.static';
import { LectorCodigosComponent } from 'src/app/compartido/lector-codigos/lector-codigos.component';
import { DomiciliosService } from 'src/app/domicilios/utilidades/domicilios.service';

@Component({
  templateUrl: './recoger-ordenes.component.html',
  styleUrls: ['./recoger-ordenes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RecogerOrdenesComponent implements OnInit {
  /* Configuración de la ficha */
  protected configFicha = deepCopy(CONFIGURACION_FICHA_RECOGER_ORDENES);

  /* ID de la organización */
  protected orgId: number | undefined;

  /* Tabla de domicilios */
  protected configTabla = deepCopy(CONFIGURACION_TABLA_RECOGER_ORDENES);
  protected dataSource: SourceTablaOrdenes;
  @ViewChild('tabla') tabla!: TablaAsincronaComponent;

  /* Órdenes recogidas */
  protected recogidas = signal(0);

  /* Estado de carga */
  protected cargando = signal(true);

  constructor(
    private readonly _ordenesService: OrdenesService,
    private readonly _domiciliosService: DomiciliosService,
    private readonly _alertasService: AlertasService,
    private readonly _dialogosService: DialogosService,
    private readonly _formulariosService: FormulariosService,
    private readonly _route: ActivatedRoute,
    protected readonly responsiveService: ResponsiveService,
  ) {
    this.dataSource = new SourceTablaOrdenes(_ordenesService);
  }

  ngOnInit(): void {
    // Agregar observables a los filtros de la tabla
    this._formulariosService.agregarValorIn(this.configTabla.filtros!, [
      'organizacion',
    ]);

    // Escuchar query params
    firstValueFrom(this._route.queryParams).then((params) => {
      const organizacion = params['organizacion'];
      if (organizacion === undefined)
        throw new ErrorFrontend('Organización no proporcionada');
      this.orgId = valorNumérico(organizacion);
      this._buscarDomicilios();
      this._cargarRecogidas$();
    });
  }

  /**
   * Realiza la búsqueda de domicilios.
   * @autor Juan Corral
   */
  private _buscarDomicilios(): void {
    if (this.orgId === undefined) return;

    // Actualizar filtros
    const filtros = this.configTabla.filtros!;
    const filtroOrg = obtenerFiltro(filtros, 'organizacion')!;
    filtroOrg.valorIn!.next(this.orgId);

    // Cargar tabla
    this.tabla.sort.active = 'fecha_creacion';
    this.tabla.sort.direction = 'asc';
  }

  /**
   * Carga las órdenes recogidas.
   * @autor Juan Corral
   */
  private async _cargarRecogidas$(): Promise<void> {
    this.cargando.set(true);

    this._ordenesService
      .obtenerOrdenes$({
        organizacion: this.orgId!,
        fecha_recogida: formatearFecha(new Date()),
      })
      .then((ordenes) => {
        this.recogidas.set(ordenes.count);
        this.cargando.set(false);
      });
  }

  /**
   * Abre el escáner de códigos de barras.
   * @autor Juan Corral
   */
  protected async abrirEscaner$(): Promise<void> {
    const configuracion: ConfiguracionDialogoVistaPrevia = {
      componente: LectorCodigosComponent,
      inputs: { tipo: 'camara' },
      botonCerrar: true,
      botonesSuperpuestos: true,
      output: 'valor',
    };
    const codigo = await this._dialogosService.abrirVistaPrevia$(configuracion);
    const id = valorNumérico(codigo);
    if (id === undefined) return;
    this.recogerOrden$(id);
  }

  /**
   * Abre el dialogo de recoger orden.
   * @param {number} id - El id de la orden a recoger.
   * @autor Juan Corral
   */
  protected async recogerOrden$(id: number): Promise<void> {
    this.cargando.set(true);
    this._alertasService.alertar('Recogiendo orden...');

    this._ordenesService
      .recogerOrden$(id)
      .then(() => {
        this._alertasService.alertarExito('Orden recogida con éxito');
        this._buscarDomicilios();
        this._cargarRecogidas$();
      })
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError(error.message);
        this.cargando.set(false);
      });
  }
}
